import {
  createView,
  CreateViewData,
  CreateViewResponse,
  getViewDetails,
  GetViewDetailsResponse,
  deleteView,
  DeleteViewData,
  DeleteViewResponse,
  updateView,
  UpdateViewData,
  UpdateViewResponse,
  listallViewsinaproject,
  ListallViewsinaprojectData,
  ListallViewsinaprojectResponse,
  type ReservedAppName,
  type ReservedViewDataApiDto,
} from '@data-access/connect/tc-tcps';
import type { Client } from '@data-access/connect/tc-issue-management';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';

export interface MayaViewerPaneState {
  readonly camera: {
    readonly lat: number;
    readonly lon: number;
    readonly fov: number;
  };
  readonly captureId: string;
  readonly sessionId: string;
  readonly stationId: string;
}

export type MayaRoutingContext =
  | { readonly type: 'view'; readonly viewId: string }
  | { readonly type: 'issue'; readonly issueId: string }
  | {
      readonly type: 'issue-view';
      readonly issueId: string;
      readonly viewId: string;
    };

export interface MayaReservedViewDataPayload {
  readonly [key: string]: unknown;
  readonly resourceId?: string;
  readonly baseUrl?: string;
  readonly floorsUrl?: string;
  readonly mayaState?: string;
  readonly current?: MayaViewerPaneState;
  readonly compare?: MayaViewerPaneState;
  readonly floorId?: string;
  readonly version?: number;
  readonly isComparing?: boolean;
}

interface MayaStateDecoded {
  readonly connectProjectId?: string;
  readonly routingContext?: MayaRoutingContext;
  readonly [key: string]: unknown;
}

export interface MayaReservedViewData
  extends Omit<
    ReservedViewDataApiDto,
    'app' | 'data' | 'modifiedBy' | 'modifiedAt'
  > {
  readonly viewId?: string;
  readonly app: 'maya' | 'Maya';
  readonly data: MayaReservedViewDataPayload;
  readonly modifiedBy?: string;
  readonly modifiedAt?: string;
  readonly updatedBy: string;
  readonly updatedAt: string;
}

export function isMayaReservedViewData(
  dto: unknown
): dto is MayaReservedViewData {
  return (
    typeof dto === 'object' &&
    dto !== null &&
    'app' in dto &&
    typeof dto.app === 'string' &&
    dto.app.toLowerCase() === 'maya'
  );
}

export function resolveFloorsUrl(floorsUrl?: string): string | undefined {
  return floorsUrl;
}

function appendQueryParam(
  params: URLSearchParams,
  key: string,
  value: unknown
): void {
  if (typeof value === 'string' && value.length > 0) {
    params.set(key, value);
    return;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    params.set(key, String(value));
  }
}

export function buildProjectSight360ViewerDeepLink(
  payload: MayaReservedViewDataPayload
): string | null {
  const { baseUrl, floorsUrl, mayaState, floorId, current } = payload;
  if (!baseUrl || !mayaState) {
    return null;
  }

  let decoded: MayaStateDecoded;
  try {
    decoded = JSON.parse(atob(mayaState)) as MayaStateDecoded;
  } catch {
    return null;
  }

  const hostState: Record<string, unknown> = { ...decoded };
  const portfolioId = hostState['PortfolioId'];
  const projectId = hostState['ProjectId'];

  const origin = baseUrl.replace(/\/$/, '');
  const params = new URLSearchParams();
  appendQueryParam(params, 'floorId', floorId);
  appendQueryParam(params, 'orgid', portfolioId);
  appendQueryParam(params, 'projid', projectId);
  appendQueryParam(params, 'floorsUrl', resolveFloorsUrl(floorsUrl));
  params.set('state', btoa(JSON.stringify(hostState)));
  appendQueryParam(params, 'sessionId', current?.sessionId);
  appendQueryParam(params, 'captureId', current?.captureId);
  appendQueryParam(params, 'stationId', current?.stationId);

  return `${origin}/Web/app/SiteCaptureViewer?${params.toString()}`;
}

export class ViewApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  // Query for getting view details
  public getViewDetailsQuery(
    viewId: Accessor<string | undefined>
  ): UseQueryResult<GetViewDetailsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['view-details', viewId()],
      queryFn: async (): Promise<GetViewDetailsResponse> => {
        const id = viewId();
        if (!id) {
          throw new Error('View ID is required');
        }

        const response = await getViewDetails({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          path: { viewId: id },
        });

        if (response.error) {
          throw new Error('Failed to get view details');
        }

        return response.data!;
      },
      enabled: !!viewId(),
    }));
  }

  // Query for listing all views in a project
  public listAllViewsInProjectQuery(
    data: Accessor<ListallViewsinaprojectData | undefined>
  ): UseQueryResult<ListallViewsinaprojectResponse, Error> {
    return useQuery(() => ({
      queryKey: ['views', data()],
      queryFn: async (): Promise<ListallViewsinaprojectResponse> => {
        const queryData = data();
        if (!queryData) {
          throw new Error('Query data is required');
        }

        const response = await listallViewsinaproject({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          query: queryData.query,
        });

        if (response.error) {
          throw new Error('Failed to list all views in project');
        }

        return response.data!;
      },
      enabled: !!data(),
    }));
  }

  // Mutation for creating a new view
  public createViewMutation(): UseMutationResult<
    CreateViewResponse,
    Error,
    CreateViewData
  > {
    return useMutation(() => ({
      mutationFn: async (data: CreateViewData): Promise<CreateViewResponse> => {
        const response = await createView({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to create view');
        }

        return response.data!;
      },
      onSuccess: () => {
        // Invalidate views list queries
        void this.queryClient.invalidateQueries({
          queryKey: ['views'],
        });
      },
    }));
  }

  // Mutation for updating a view
  public updateViewMutation(): UseMutationResult<
    UpdateViewResponse,
    Error,
    UpdateViewData
  > {
    return useMutation(() => ({
      mutationFn: async (data: UpdateViewData): Promise<UpdateViewResponse> => {
        const response = await updateView({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to update view');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Invalidate views list and specific view details
        void this.queryClient.invalidateQueries({
          queryKey: ['views'],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['view-details', variables.path.viewId],
        });
      },
    }));
  }

  // Mutation for deleting a view
  public deleteViewMutation(): UseMutationResult<
    DeleteViewResponse,
    Error,
    DeleteViewData
  > {
    return useMutation(() => ({
      mutationFn: async (data: DeleteViewData): Promise<DeleteViewResponse> => {
        const response = await deleteView({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to delete view');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Invalidate views list and remove specific view details from cache
        void this.queryClient.invalidateQueries({
          queryKey: ['views'],
        });
        this.queryClient.removeQueries({
          queryKey: ['view-details', variables.path.viewId],
        });
      },
    }));
  }

  /**
   * Fetch app-specific reserved data for a view.
   * Returns null if the view has no reserved data for the given app.
   */
  public async getReservedViewData(
    viewId: string,
    appName: ReservedAppName
  ): Promise<ReservedViewDataApiDto | null> {
    const response = await this.client.get({
      url: '/views/{viewId}/reserved',
      path: { viewId },
      query: { app: appName },
    });

    if (response.error) {
      return null;
    }

    return response.data as ReservedViewDataApiDto;
  }

  /**
   * Store app-specific reserved data for a view.
   * Fire-and-forget safe — callers should catch errors without blocking the view creation flow.
   */
  public async saveReservedViewData(
    viewId: string,
    appName: ReservedAppName,
    data: Record<string, unknown>
  ): Promise<ReservedViewDataApiDto> {
    const response = await this.client.put({
      url: '/views/{viewId}/reserved',
      path: { viewId },
      query: { app: appName },
      body: data,
    });

    if (response.error) {
      throw new Error('Failed to save reserved view data');
    }

    return response.data as ReservedViewDataApiDto;
  }

  /**
   * Delete app-specific reserved data for a view.
   */
  public async deleteReservedViewData(
    viewId: string,
    appName: ReservedAppName
  ): Promise<void> {
    const response = await this.client.delete({
      url: '/views/{viewId}/reserved',
      path: { viewId },
      query: { app: appName },
    });

    if (response.error) {
      throw new Error('Failed to delete reserved view data');
    }
  }
}
